import clsx from 'clsx'
import { Autocomplete } from 'components/utility/autocomplete'
import React, { useEffect, useMemo } from 'react'
import { InputField } from '../Components/inputField'
import PricingNote from '../Components/pricingNote'

const CONTACT_FIELDS = [
  {
    name: 'buyer_name',
    label: 'requester name',
    placeholder: 'full name',
    required: true,
    type: 'text',
  },
  {
    name: 'buyer_email',
    label: 'email',
    placeholder: 'email',
    required: true,
    type: 'email',
  },
  {
    name: 'buyer_phone',
    label: 'phone number',
    placeholder: 'XXX-XXX-XXXX',
    required: true,
    type: 'text',
  },
  {
    name: 'company_name',
    label: 'bank or company name (if applicable)',
    type: 'text',
    placeholder: (input) => input?.company?.name,
    inputClasses: 'company-dark-placeholder',
  },
  {
    name: 'comment',
    label: 'add comment',
    placeholder: 'comment',
    type: 'textarea',
  },
]

export default function RequestInfoFormSection({
  onBack,
  onSubmit,
  building,
  buildingStructures,
  company,
  values,
  setValues,
  errors,
  onChange,
  onBlur,
  isDirty,
  isValid,
  validateForm,
  handleError,
}) {
  const handleBuildingSelect = (building) => {
    setValues((prevValues) => {
      const updatedValues = {
        ...prevValues,
        unit_id: null,
        units: building.building_units,
        building_structure_id: building.id,
      }
      return updatedValues
    })
    handleError({ name: 'building_structure_id', value: building.id })
  }

  const handleUnitSelect = (unit) => {
    setValues((prev) => ({ ...prev, unit_id: unit.unit_number }))
    handleError({ name: 'unit_id', value: unit.unit_number })
  }

  const handleUnitOnChange = (e) => {
    setValues((prev) => ({ ...prev, unit_id: e.target.value }))
    handleError({ name: 'unit_id', value: e.target.value })
  }

  useEffect(() => {
    // reset building and units selections when user leaves this component
    return () => {
      setValues((prevValues) => {
        const updatedValues = {
          ...prevValues,
          building_structure_id: null,
          unit_id: null,
          units: [],
        }
        return updatedValues
      })
    }
  }, [])

  useEffect(() => {
    // if there's only one building structure, select it by default
    if (buildingStructures?.length === 1) handleBuildingSelect(buildingStructures[0])
  }, [buildingStructures])

  const fieldData = useMemo(
    () => ({
      company,
    }),
    [company]
  )

  return (
    <div>
      <div style={{ margin: '3rem 0 2.4rem 0' }} className="dome-d-flex dome-flex-column dome-gap30">
        {/*TODO: Display pricing estimation*/}
        {/*<PricingNote />*/}
        <div className="dome-d-flex dome-flex-column dome-gap6">
          <span className="dome-p1 dome-color-darkest-grey dome-text-w500">last step!</span>
          <span className="dome-p2 dome-color-med-grey">
            Please enter your unit and contact information. You’ll receive an email with further instructions.
          </span>
        </div>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          const errors = validateForm()
          const validForm = Object.keys(errors).length === 0
          if (!validForm) return
          onSubmit(e)
        }}
        className="form-request-info"
      >
        <div className="dome-d-flex dome-flex-column dome-gap12">
          <div className="dome-d-flex dome-flex-column dome-gap6">
            <div>
              <label className="dome-p3 dome-color-med-grey">building address *</label>
              <Autocomplete
                placeholder="building address"
                options={buildingStructures}
                onSelect={handleBuildingSelect}
                getOptionLabel={(item) => item.default_additional_address.address}
                getValueLabel={() =>
                  buildingStructures?.find((e) => e.id === values['building_structure_id'])?.default_additional_address
                    ?.address
                }
                error={errors['building_structure_id']}
              />
            </div>
            <div>
              <label className="dome-p3 dome-color-med-grey">unit *</label>
              <Autocomplete
                searchable={true}
                placeholder="unit value"
                options={values['units']}
                filterOptions={(unit) => unit.unit_number.includes(values['unit_id'] !== null ? values['unit_id'] : '')}
                onSelect={handleUnitSelect}
                onInputChange={handleUnitOnChange}
                getOptionLabel={(item) => item.unit_number}
                getValueLabel={() => (values['unit_id'] !== null ? values['unit_id'] : '')}
                error={errors['unit_id']}
              />
            </div>
          </div>
          <span style={{ marginTop: '20px' }} className="dome-p1 dome-color-darkest-grey dome-text-w500">
            requester info
          </span>
          <div className="dome-d-flex dome-flex-column dome-gap6">
            {CONTACT_FIELDS.map((field) => {
              const placeholder =
                typeof field.placeholder === 'function' ? field.placeholder(fieldData) : field.placeholder

              return (
                <InputField
                  key={field.name}
                  {...field}
                  onBlur={onBlur}
                  onChange={onChange}
                  value={values[field.name]}
                  error={errors[field.name]}
                  placeholder={placeholder}
                  inputClasses={field.inputClasses}
                />
              )
            })}
          </div>
        </div>
        <div style={{ marginTop: '30px' }} className="dome-d-flex dome-align-center dome-justify-between">
          <button
            type="button"
            onClick={onBack}
            className="dome-btn dome-btn-link dome-btn-link-med-grey dome-btn-link-chevron-left"
          >
            change form
          </button>
          <button
            type="submit"
            disabled={!isValid || !isDirty}
            className={clsx(
              'dome-btn dome-btn-base',
              !isValid || !isDirty ? 'dome-btn-disabled' : 'dome-btn-dark-green '
            )}
          >
            request questionnaire
          </button>
        </div>
      </form>
    </div>
  )
}
